import React from 'react';
import type { TenantCardTenant } from './TenantCard';
import {
  User,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clipboard,
  Clock,
  Gavel,
  Home,
  ExternalLink,
  X,
  Zap, // Using Zap for the 'Current' indicator instead of CheckCircle
} from 'lucide-react'; // Replace with Heroicons imports if necessary

// Placeholder for external utility function
const formatDateIE = (dateString: string) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IE', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'Invalid Date';
  }
};

interface EvictionRoadmapModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant: TenantCardTenant;
  propertyId: number | string;
  tenancyMonths: number;
}

const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

// --- START: Timeline Step Component (Using Heroicons style classes) ---
interface TimelineStepProps {
  step: number;
  title: string;
  children: React.ReactNode;
  icon: React.FC<any>;
  isCurrent?: boolean;
}

const TimelineStep: React.FC<TimelineStepProps> = ({
  step,
  title,
  children,
  icon: Icon,
  isCurrent = false,
}) => {
  return (
    <div className="flex relative">
      {/* Vertical Line Connector */}
      <div className="flex flex-col items-center mr-4">
        <div className="w-px h-full bg-blue-200" />
        <div
          className={`z-10 w-8 h-8 flex items-center justify-center rounded-full shadow-md transition-all duration-300 ${
            isCurrent
              ? 'bg-red-500 text-white ring-4 ring-red-200' // Current Step highlight
              : 'bg-blue-50 text-blue-600 border border-blue-200' // Default Step
          }`}
        >
          {isCurrent ? <Zap className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <div className="w-px h-full bg-blue-200" />
      </div>

      {/* Content Card */}
      <div
        className={`flex-1 pt-0 pb-6 transition-all duration-300 ${
          isCurrent ? 'scale-[1.01] rounded-xl' : ''
        }`}
      >
        <div
          className={`p-4 rounded-xl border transition-colors duration-300 ${
            isCurrent
              ? 'bg-red-50 border-red-300 shadow-lg'
              : 'bg-white border-gray-200 hover:border-blue-300'
          }`}
        >
          <p
            className={`text-xs font-bold uppercase tracking-wider ${
              isCurrent ? 'text-red-600' : 'text-blue-500'
            }`}
          >
            Step {step}
          </p>
          <h4
            className={`mt-1 text-base font-semibold ${
              isCurrent ? 'text-red-900' : 'text-gray-900'
            }`}
          >
            {title}
          </h4>
          <div className="mt-2 text-sm text-gray-700">{children}</div>
        </div>
      </div>
    </div>
  );
};
// --- END: Timeline Step Component ---

const EvictionRoadmapModal: React.FC<EvictionRoadmapModalProps> = ({
  isOpen,
  onClose,
  tenant,
  propertyId,
  tenancyMonths,
}) => {
  if (!isOpen) return null;

  const isRentArrears = tenant.paymentStatus === 'late';
  const today = new Date();
  const warningExpires = addDays(today, 28);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity"
      aria-labelledby="eviction-roadmap-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal Container with max-w-4xl for wider content */}
      <div className="relative z-10 w-full max-w-4xl rounded-3xl bg-white shadow-2xl max-h-[90vh] flex flex-col transform transition-transform scale-100">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-blue-100 bg-blue-50 rounded-t-3xl">
          <div>
            <h3 id="eviction-roadmap-title" className="text-xl font-bold text-blue-800">
              🇮🇪 Legal Eviction Roadmap (Ireland)
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              **Informational outline only.** This is a complex legal process; always confirm steps
              with the Residential Tenancies Board (RTB) and seek independent legal advice.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 inline-flex items-center rounded-full p-2 text-gray-500 hover:bg-white hover:text-gray-900 transition-colors"
            aria-label="Close eviction roadmap"
          >
            <X className="w-6 h-6" /> {/* Heroicon-style close icon */}
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Summary Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Tenant Card */}
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start space-x-3">
              <User className="w-6 h-6 text-blue-600 mt-0.5" />
              <div>
                <p className="text-xs font-semibold uppercase text-blue-600 tracking-wider">
                  Tenant
                </p>
                <p className="text-lg font-medium text-gray-900">{tenant.name}</p>
                <p className="text-sm text-gray-600">Email: {tenant.email || '—'}</p>
                <p className="text-sm text-gray-600">Phone: {tenant.phone || '—'}</p>
              </div>
            </div>

            {/* Tenancy Card */}
            <div
              className={`rounded-xl border p-4 flex items-start space-x-3 ${isRentArrears ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}
            >
              <Calendar
                className={`w-6 h-6 mt-0.5 ${isRentArrears ? 'text-red-600' : 'text-green-600'}`}
              />
              <div>
                <p
                  className={`text-xs font-semibold uppercase tracking-wider ${isRentArrears ? 'text-red-600' : 'text-green-600'}`}
                >
                  Tenancy Summary
                </p>
                <p className="text-lg font-medium text-gray-900">
                  Start: {formatDateIE(tenant.moveInDate)}
                </p>
                <p className="text-sm text-gray-600">Duration: **~{tenancyMonths} months**</p>
                <p className="text-sm text-gray-600">
                  Lease end: {tenant.leaseEndDate ? formatDateIE(tenant.leaseEndDate) : 'Not set'}
                </p>
                <p
                  className={`text-sm font-semibold mt-1 ${isRentArrears ? 'text-red-700' : 'text-green-700'}`}
                >
                  Payment Status: {tenant.paymentStatus || '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Roadmap Steps - Using Timeline structure */}
          <div className="space-y-0 pt-2">
            <TimelineStep step={1} title="Assess Legal Grounds & Gather Evidence" icon={Gavel}>
              <ul className="mt-1 list-disc pl-5 text-gray-700 space-y-1">
                <li>
                  Confirm one of the legal, statutory grounds for termination (e.g., rent arrears,
                  breach of obligations, landlord intention to sell, etc.).
                </li>
                {isRentArrears && (
                  <li className="font-medium text-red-700">
                    <AlertTriangle className="inline w-4 h-4 mr-1 mb-0.5" /> **HIGH PRIORITY:** Rent
                    arrears flagged. The 28-day warning process is mandatory.
                  </li>
                )}
                <li>
                  Gather necessary documentation (tenancy agreement, payment ledger, breach
                  evidence, planning permission, etc.).
                </li>
              </ul>
            </TimelineStep>

            <TimelineStep step={2} title="Determine Statutory Notice Period" icon={Clock}>
              <p className="mt-1 text-gray-700">
                The minimum notice period is determined by the **length of the tenancy**. Use RTB
                guidance to calculate the correct minimum period based on the **~{tenancyMonths}{' '}
                months** in situ. Shorter, non-tenancy-length periods apply for certain grounds
                (e.g., serious anti-social behaviour).
              </p>
            </TimelineStep>

            <TimelineStep
              step={3}
              title="Issue Rent Arrears Warning (If Applicable)"
              icon={Clipboard}
              isCurrent={isRentArrears}
            >
              {isRentArrears ? (
                <>
                  <p className="mt-1 text-gray-700">
                    Serve a written **28-day warning notice** requiring payment of arrears *before*
                    a Notice of Termination can be served. The termination notice can only be served
                    after this period if arrears remain.
                  </p>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3 text-center">
                    <div className="rounded-md border border-red-300 bg-white p-3 text-sm text-gray-700">
                      <p className="font-semibold text-red-700">Warning Served (Today)</p>
                      <p className="font-bold">{formatDateIE(today.toISOString())}</p>
                    </div>
                    <div className="rounded-md border border-red-300 bg-white p-3 text-sm text-gray-700">
                      <p className="font-semibold text-red-700">28-Day Warning Expires</p>
                      <p className="font-bold">{formatDateIE(warningExpires.toISOString())}</p>
                    </div>
                    <div className="rounded-md border border-red-300 bg-white p-3 text-sm text-gray-700">
                      <p className="font-semibold text-red-700">Earliest Termination Notice Date</p>
                      <p className="font-bold">{formatDateIE(warningExpires.toISOString())}</p>
                    </div>
                  </div>
                </>
              ) : (
                <p className="mt-1 text-gray-700">
                  Skipped, as payment status is not flagged as 'late'. If the tenant is in arrears,
                  this step is mandatory.
                </p>
              )}
            </TimelineStep>

            <TimelineStep step={4} title="Serve Notice of Termination (NOT)" icon={Home}>
              <ul className="mt-1 list-disc pl-5 text-gray-700 space-y-1">
                <li>
                  The NOT must be **in writing**, signed by the landlord/agent, and specify the
                  termination date and ground for termination.
                </li>
                <li>
                  It must state that the tenant has the right to refer a dispute to the RTB within
                  **28 days** from the receipt of the notice.
                </li>
                <li>
                  Use a provable service method (e.g., registered post or hand delivery with a
                  witness).
                </li>
              </ul>
            </TimelineStep>

            <TimelineStep step={5} title="Monitor & RTB Dispute Resolution" icon={Gavel}>
              <ul className="mt-1 list-disc pl-5 text-gray-700 space-y-1">
                <li>Wait for the notice period to expire.</li>
                <li>
                  If the tenant disputes the notice, you must fully engage with the RTB's dispute
                  resolution process (mediation or adjudication).
                </li>
                <li>
                  If the tenant remains after the termination date and a Determination Order has
                  been issued by the RTB, a District Court order is required for enforcement
                  (repossession).
                </li>
              </ul>
            </TimelineStep>

            <TimelineStep step={6} title="Vacant Possession & Deposit Planning" icon={CheckCircle}>
              <ul className="mt-1 list-disc pl-5 text-gray-700 space-y-1">
                <li>
                  Arrange final property inspection, document condition, and reconcile the security
                  deposit.
                </li>
                <li>
                  Return the remaining deposit (or explain deductions in writing) within 10 working
                  days of the termination of the tenancy.
                </li>
                <li>Document final meter readings and update RTB registration records.</li>
              </ul>
            </TimelineStep>

            {/* Resources Block */}
            <div className="mt-6 p-4 rounded-xl bg-gray-50 border border-gray-200">
              <p className="text-sm font-semibold text-gray-900 flex items-center">
                <ExternalLink className="w-5 h-5 mr-2 text-blue-500" />
                Official Guidance & Procedures (Residential Tenancies Board - RTB)
              </p>
              <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li>
                  <a
                    href="https://rtb.ie/renting/ending-a-tenancy/notice-of-termination-guide/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    RTB: Notice of Termination (Full Guide & Requirements)
                  </a>
                </li>
                <li>
                  <a
                    href="https://rtb.ie/disputes/disputes-process/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    RTB: Dispute Resolution Process (Mediation/Adjudication)
                  </a>
                </li>
              </ul>
              <p className="mt-3 text-xs text-gray-600">
                **Disclaimer:** Always verify the latest statutory requirements and seek legal
                advice where necessary. Laws are subject to change. Property ID:{' '}
                {String(propertyId)}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-gray-200 p-5 gap-3 rounded-b-3xl">
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-xl border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Close Roadmap
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-xl bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 shadow-md transition-colors"
          >
            Acknowledge & Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvictionRoadmapModal;